import Notification from '@rio-cloud/rio-uikit/Notification';
import type React from 'react';
import { useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import type { FleetAdminRequest } from '../../../../../app/types';
import { useUpdateAccountMutation } from '../../../../../services/accountsApi';
import type { Account } from '../../../../../services/schemas/accountsBackendCodec';
import type { UserGroup } from '../../../../../services/schemas/userAdminBackendCodec';
import { useInviteUserMutation, useLazyFetchGroupsInAccountQuery } from '../../../../../services/usersApi';
import { AddUserDialog } from '../../dialogs/AddUserDialog';

export const AddUserToAccount = ({ account }: { account: Account }) => {
    const [showDialog, setShowDialog] = useState(false);
    const [managedGroups, setManagedGroups] = useState<UserGroup[]>([]);

    const [inviteUserRtk] = useInviteUserMutation();
    const [fetchGroupsInAccount] = useLazyFetchGroupsInAccountQuery();
    const [
        updateAccountRtk,
        // { isLoading: updateIsLoading, isSuccess: updateIsSuccess, isError: updateIsError, error: updateError },
    ] = useUpdateAccountMutation();

    const intl = useIntl();

    const handleClick = async (evt: React.MouseEvent<HTMLElement>) => {
        evt.stopPropagation();
        try {
            const groups = await fetchGroupsInAccount({ accountId: account.id }).unwrap();
            setManagedGroups(groups.filter(group => group.metadata?.is_managed_group === true));
            setShowDialog(true);
        } catch (error) {
            console.error('Could not load managed groups:', error);
            Notification.error('Could not load managed groups for this account.');
        }
    };

    const handleAddFltAdminConfirm = async (fleetAdminReq: FleetAdminRequest) => {
        if (
            fleetAdminReq.firstname === undefined ||
            fleetAdminReq.lastname === undefined ||
            fleetAdminReq.email === undefined ||
            fleetAdminReq.reason === undefined ||
            fleetAdminReq.groupId === undefined
        ) {
            Notification.error(
                'Encountered a problem with the input values. Please make sure to input all required fields.'
            );
            return;
        }

        try {
            if (fleetAdminReq.isGeneralContact || fleetAdminReq.isBillingContact) {
                const accountToUpdate = {
                    ...account,
                    contacts: {
                        general: fleetAdminReq.isGeneralContact
                            ? { email: fleetAdminReq.email }
                            : account.contacts?.general,
                        billing: fleetAdminReq.isBillingContact
                            ? { email: fleetAdminReq.email }
                            : account.contacts?.billing,
                    },
                };
                await updateAccountRtk({
                    account: accountToUpdate,
                    reason: `${fleetAdminReq.reason} - Additionally update account contact information`,
                }).unwrap();
            }
            await inviteUserRtk({
                user: {
                    firstName: fleetAdminReq.firstname,
                    lastName: fleetAdminReq.lastname,
                    accountId: account.id,
                    accountType: account.accountType,
                    email: fleetAdminReq.email,
                    groupIds: [fleetAdminReq.groupId],
                    phoneNumber: fleetAdminReq.phoneNumber?.replace(/ /g, ''),
                    preferredLanguage: fleetAdminReq.language,
                },
                reason: fleetAdminReq.reason,
            }).unwrap();
            Notification.success(intl.formatMessage({ id: 'intl-msg:helpgang.notification.inviteFleetAdminSuccess' }));
        } catch (error) {
            console.error('Could not invite fleet admin:', error);
            handleError(error, account.id, intl);
        } finally {
            setShowDialog(false);
        }
    };

    return (
        <>
            <button type={'button'} className={'btn btn-primary'} onClick={handleClick}>
                Add User
            </button>
            <AddUserDialog
                accountName={account.name}
                groups={managedGroups}
                showDialog={showDialog}
                setShowDialog={setShowDialog}
                handleAddAdminConfirm={handleAddFltAdminConfirm}
            />
        </>
    );
};

const handleError = (error: unknown, accountId: string, intl: IntlShape) => {
    if (error !== null && typeof error === 'object' && 'error' in error && typeof error.error === 'string') {
        if (error.error === 'Could not find fleet admin group') {
            Notification.error(`Did not find fleet admin group in account ${accountId}. Please report this issue.`);
        } else if (error.error.startsWith('Error: 409')) {
            if (
                'data' in error &&
                typeof error.data === 'string' &&
                error.data.includes('USERNAME_ALREADY_EXISTS_IN_ACCOUNT')
            ) {
                Notification.error(
                    ` 
                    USERNAME_ALREADY_EXISTS_IN_ACCOUNT 
                    Email or phone number already exists in this account.`,
                    { title: 'Error:' }
                );
            } else {
                Notification.error(error.error);
            }
        } else {
            Notification.error(intl.formatMessage({ id: 'intl-msg:helpgang.notification.inviteFleetAdminFailure' }));
        }
    } else {
        Notification.error(intl.formatMessage({ id: 'intl-msg:helpgang.notification.inviteFleetAdminFailure' }));
    }
};
